/**
 * ── One candidate's interview trail (the candidate panel) ───────────────────
 *
 * Every round this candidate has had or has coming, cancellations and moves included, fetched
 * only when the panel is opened. Nothing here is part of the board payload.
 *
 * Both sources are read server-side, because Lever alone is wrong in a predictable direction: our
 * Lever key cannot delete panels, so a cancellation can leave a panel standing with nothing on it
 * to say it was cancelled. See proxy-server/lib/candidateSchedule.js.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';
const BASE = `${PROXY_URL}/api/ta-action-board`;

/** Why an interview did not happen. Internal wording — a recruiter's own screen only. */
export interface InterviewCancellation {
  at: string | null;
  /** The staff member who cancelled. Null on the candidate's own path, where there is none. */
  by: string | null;
  /** 'candidate' | 'finn' — who dropped it, when our record knows. */
  source: string | null;
  reason: string | null;
}

export interface TrailInterview {
  id: string;
  /** The round's name, e.g. "First Interview". Null when the stage list was unavailable. */
  round: string | null;
  at: string | null;
  endAt: string | null;
  interviewers: string[];
  status: 'upcoming' | 'held' | 'cancelled';
  cancellation: InterviewCancellation | null;
  /** How many times this interview moved before it got here. 0 is not shown. */
  rescheduleCount: number;
  bookedAt: string | null;
}

export interface CandidateSchedule {
  /** Newest first. */
  interviews: TrailInterview[];
  /** False when our own booking records could not be read — the trail may be missing moves. */
  ourRecordsOk: boolean;
}

/** A deliberate refusal (403) — shown as a sentence, never retried. */
export class ScheduleRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScheduleRefusedError';
  }
}

export async function fetchCandidateSchedule(input: {
  opportunityId: string;
  boardEmail: string;
}): Promise<CandidateSchedule> {
  const qs = new URLSearchParams({ opportunityId: input.opportunityId, board: input.boardEmail });
  try {
    const res = await authenticatedFetch(`${BASE}/candidate-schedule?${qs}`, {
      // A card left over from a board switch is an expected answer, not an error to report.
      suppressErrorStatuses: [403],
    });
    if (res.status === 403) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      void logAnalyticsEvent('candidate_schedule_opened', { result: 'not_on_board' });
      throw new ScheduleRefusedError(body.error || 'This candidate is not on your board');
    }
    const data = (await res.json()) as CandidateSchedule;
    void logAnalyticsEvent('candidate_schedule_opened', {
      result: 'ok',
      interviews: data.interviews.length,
      our_records: data.ourRecordsOk,
    });
    return data;
  } catch (error) {
    if (!(error instanceof ScheduleRefusedError)) {
      void logAnalyticsEvent('candidate_schedule_opened', { result: 'error' });
    }
    throw error;
  }
}
